export const CLIENT_REGISTRATION_ADMIN_STATUS_VALUES = [
  "RECEIVED",
  "TRIAGE",
  "AWAITING_CHANNEL",
  "APPROVED",
  "IN_PARAMETERIZATION",
  "READY_FOR_ACTIVATION",
  "ACTIVATED",
  "REJECTED",
  "CANCELLED",
  "INTEGRATION_FAILURE",
] as const;

export type ClientRegistrationAdminStatus =
  (typeof CLIENT_REGISTRATION_ADMIN_STATUS_VALUES)[number];

export const CLIENT_REGISTRATION_CHANNEL_STATUS_VALUES = [
  "RASCUNHO",
  "ENVIADA",
  "EM_ANALISE",
  "PENDENTE_COMPLEMENTACAO",
  "APROVADA_PARA_IMPLANTACAO",
  "EM_IMPLANTACAO",
  "APTO_PARA_OPERAR",
  "REPROVADA",
  "CANCELADA",
  "FALHA_INTEGRACAO",
] as const;

export type ClientRegistrationChannelStatus =
  (typeof CLIENT_REGISTRATION_CHANNEL_STATUS_VALUES)[number];

export const CLIENT_REGISTRATION_DESK_STATUS_VALUES = [
  "PENDING",
  "CREATED",
  "FAILED",
  "REPROCESSING",
] as const;

export type ClientRegistrationDeskStatus =
  (typeof CLIENT_REGISTRATION_DESK_STATUS_VALUES)[number];

export const CLIENT_REGISTRATION_EVENT_VISIBILITY_VALUES = [
  "CHANNEL",
  "INTERNAL",
] as const;

export type ClientRegistrationEventVisibility =
  (typeof CLIENT_REGISTRATION_EVENT_VISIBILITY_VALUES)[number];

export const CLIENT_REGISTRATION_EVENT_TYPE_VALUES = [
  "SUBMITTED",
  "DESK_CREATED",
  "DESK_FAILED",
  "COMPLEMENT_REQUESTED",
  "COMPLEMENT_ANSWERED",
  "APPROVED",
  "ASSIGNED",
  "PARAMETERIZATION_STARTED",
  "CHECKLIST_UPDATED",
  "READY_FOR_ACTIVATION",
  "ACTIVATED",
  "REJECTED",
  "CANCELLED",
  "NOTE",
] as const;

export type ClientRegistrationEventType =
  (typeof CLIENT_REGISTRATION_EVENT_TYPE_VALUES)[number];

export type ClientRegistrationActivationChecklist = {
  clientConfigured: boolean;
  contractConfigured: boolean;
  servicesConfigured: boolean;
  integrationsConfigured: boolean;
  finalValidationCompleted: boolean;
};

export const EMPTY_CLIENT_REGISTRATION_ACTIVATION_CHECKLIST: ClientRegistrationActivationChecklist = {
  clientConfigured: false,
  contractConfigured: false,
  servicesConfigured: false,
  integrationsConfigured: false,
  finalValidationCompleted: false,
};

export const CLIENT_REQUESTED_SERVICE_OPTIONS = [
  { value: "NR1", label: "NR1" },
  { value: "TELEMEDICINA", label: "Telemedicina" },
  { value: "APS_DIGITAL", label: "APS Digital" },
  { value: "APS_FISICA", label: "APS Física" },
  { value: "BASE_DE_VIDAS", label: "Base de Vidas" },
  { value: "FATURAMENTO", label: "Faturamento" },
] as const;

export const CLIENT_REGISTRATION_TERMINAL_ADMIN_STATUS_VALUES = [
  "ACTIVATED",
  "REJECTED",
  "CANCELLED",
] as const;

export type ClientRegistrationTerminalAdminStatus =
  (typeof CLIENT_REGISTRATION_TERMINAL_ADMIN_STATUS_VALUES)[number];

const ADMIN_STATUS_LABELS: Record<ClientRegistrationAdminStatus, string> = {
  RECEIVED: "Recebida",
  TRIAGE: "Em triagem",
  AWAITING_CHANNEL: "Aguardando complemento do Canal",
  APPROVED: "Aprovada para implantação",
  IN_PARAMETERIZATION: "Em parametrização",
  READY_FOR_ACTIVATION: "Pronta para ativação",
  ACTIVATED: "Ativada",
  REJECTED: "Reprovada",
  CANCELLED: "Cancelada",
  INTEGRATION_FAILURE: "Falha de integração",
};

const CHANNEL_STATUS_LABELS: Record<ClientRegistrationChannelStatus, string> = {
  RASCUNHO: "Rascunho",
  ENVIADA: "Enviada",
  EM_ANALISE: "Em análise",
  PENDENTE_COMPLEMENTACAO: "Pendente de complementação",
  APROVADA_PARA_IMPLANTACAO: "Aprovada para implantação",
  EM_IMPLANTACAO: "Em implantação",
  APTO_PARA_OPERAR: "Apto para operar",
  REPROVADA: "Reprovada",
  CANCELADA: "Cancelada",
  FALHA_INTEGRACAO: "Falha de integração",
};

const DESK_STATUS_LABELS: Record<ClientRegistrationDeskStatus, string> = {
  PENDING: "Pendente",
  CREATED: "Chamado criado",
  FAILED: "Falha de integração",
  REPROCESSING: "Reprocessando",
};

export function getClientRegistrationAdminStatusLabel(
  status: ClientRegistrationAdminStatus,
) {
  return ADMIN_STATUS_LABELS[status] ?? status;
}

export function getClientRegistrationChannelStatusLabel(
  status: ClientRegistrationChannelStatus,
) {
  return CHANNEL_STATUS_LABELS[status] ?? status;
}

export function getClientRegistrationChannelDisplayLabel(
  adminStatus: ClientRegistrationAdminStatus,
  channelStatus: ClientRegistrationChannelStatus,
) {
  if (adminStatus === "READY_FOR_ACTIVATION") {
    return "Implantação concluída, aguardando liberação final";
  }

  return getClientRegistrationChannelStatusLabel(channelStatus);
}

export function getClientRegistrationDeskStatusLabel(
  status: ClientRegistrationDeskStatus,
) {
  return DESK_STATUS_LABELS[status] ?? status;
}

export function isClientRegistrationTerminalAdminStatus(
  status: ClientRegistrationAdminStatus | null | undefined,
) {
  if (!status) {
    return false;
  }

  return CLIENT_REGISTRATION_TERMINAL_ADMIN_STATUS_VALUES.includes(
    status as ClientRegistrationTerminalAdminStatus,
  );
}

export function mapAdminStatusToChannelStatus(
  status: ClientRegistrationAdminStatus,
): ClientRegistrationChannelStatus {
  switch (status) {
    case "RECEIVED":
      return "ENVIADA";
    case "TRIAGE":
      return "EM_ANALISE";
    case "AWAITING_CHANNEL":
      return "PENDENTE_COMPLEMENTACAO";
    case "APPROVED":
      return "APROVADA_PARA_IMPLANTACAO";
    case "IN_PARAMETERIZATION":
    case "READY_FOR_ACTIVATION":
      return "EM_IMPLANTACAO";
    case "ACTIVATED":
      return "APTO_PARA_OPERAR";
    case "REJECTED":
      return "REPROVADA";
    case "CANCELLED":
      return "CANCELADA";
    case "INTEGRATION_FAILURE":
      return "FALHA_INTEGRACAO";
    default:
      return "ENVIADA";
  }
}

export function isClientRegistrationActivationChecklistComplete(
  checklist: Partial<ClientRegistrationActivationChecklist> | null | undefined,
) {
  if (!checklist) {
    return false;
  }

  return Boolean(
    checklist.clientConfigured &&
      checklist.contractConfigured &&
      checklist.servicesConfigured &&
      checklist.integrationsConfigured &&
      checklist.finalValidationCompleted,
  );
}

export const CLIENT_REGISTRATION_ADMIN_STATUS_OPTIONS = CLIENT_REGISTRATION_ADMIN_STATUS_VALUES.map((value) => ({
  value,
  label: getClientRegistrationAdminStatusLabel(value),
}));

export const CLIENT_REGISTRATION_CHANNEL_STATUS_OPTIONS = CLIENT_REGISTRATION_CHANNEL_STATUS_VALUES.map((value) => ({
  value,
  label: getClientRegistrationChannelStatusLabel(value),
}));
